import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';

import { BUTTON_SIZES, PrimaryButton } from 'js/src/components/common/ui/Button';
import ContentfulImageOrVideo from 'js/src/components/public-pages/ContentfulImageOrVideo';
import type { ImmutableMap } from 'js/src/libs/consts';
import type { ContentfulImageOrVideoData } from 'js/src/libs/contentful-utils';
import { getColClassNames, TextWidth } from 'js/src/libs/contentful-utils';

type Props = {
    data: ImmutableMap<{
        paragraph: Document;
        headline: string;
        image: ContentfulImageOrVideoData;
        image_side: string;
        cta_url?: string;
        cta_text?: string;
        poster_for_video: ContentfulImageOrVideoData;
        text_width?: TextWidth;
    }>;
    bgColor: string;
    lazyLoad: boolean;
};

const Hero = React.memo(function Hero({ data, bgColor, lazyLoad }: Props) {
    let paragraph = null;
    if (data.get('paragraph')) {
        paragraph = documentToReactComponents(data.get('paragraph').toJS());
    }

    let cta = null;

    const ctaUrl = data.get('cta_url');
    const ctaText = data.get('cta_text');

    if (ctaUrl && ctaText) {
        let url = ctaUrl;
        const hasQueryString = url.includes('?');

        if (!hasQueryString) {
            // Automatically populate utm_source, utm_medium, and utm_campaign
            // so that we can track the performance of the CTA
            const utmCampaign = ctaText
                .toLowerCase()
                .split(' ')
                .slice(0, 3)
                .join('_')
                .replace(/[^a-zA-Z0-9_]/g, '_');
            url += `?utm_source=lumen5_website&utm_medium=content&utm_campaign=${utmCampaign}`;
        }

        cta = <PrimaryButton href={url} size={BUTTON_SIZES.LG} text={ctaText} />;
    }

    const isImageLeft = (data.get('image_side') || '').toString().toLowerCase() === 'left';
    const textWidth = data.get('text_width') || TextWidth.STANDARD;

    const textInner = <div className="font-l5 font-l5--regular">{paragraph}</div>;

    const textBlock = (
        <>
            <h3>{data.get('headline')}</h3>
            {textInner}
            {cta}
        </>
    );

    const imageBlock = (
        <ContentfulImageOrVideo
            data={data.get('image')}
            posterForVideo={data.get('poster_for_video')}
            urlWidth={800}
            style={{ width: '100%' }}
            lazyLoad={lazyLoad}
        />
    );

    const { leftMd, rightMd } = getColClassNames(textWidth, isImageLeft);

    const leftContent = isImageLeft ? imageBlock : textBlock;
    const rightContent = isImageLeft ? textBlock : imageBlock;

    return (
        <div className="contentful-component hero" style={{ backgroundColor: bgColor }}>
            <div className="container">
                <div className="row">
                    <div className={`my-l5-24 ${leftMd} align-self-center`}>
                        <div className="me-l5-48">{leftContent}</div>
                    </div>
                    <div className={`my-l5-24 ${rightMd} align-self-center`}>
                        <div className="ms-l5-48">{rightContent}</div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Hero;
